import { instance } from "api/apiBase";
import { Paginated } from "types/common/paginated";
import { Lead, LeadType } from "views/leads-id/types";
import { Property, Unit } from "types/landlord/address";

export type DateRangeParams = {
  start_date?: string;
  end_date?: string;
  unit_ids?: number[];
  property_ids?: number[];
  property_group_ids?: number[];
  // Scopes lead-based analytics to a single pipeline (tenant vs owner). Honored
  // by the overview, leads_by_source, leads, lead_pipeline, and
  // lead_pipeline_drilldown endpoints (boompay-api #4574).
  lead_type?: LeadType;
};

type LeadPipelineUnit = Pick<Unit, "id" | "title" | "listed"> & {
  available_for_application: boolean;
};

export type FilterUnit = Pick<
  LeadPipelineUnit,
  "id" | "title" | "available_for_application"
>;

export type FilterProperty = {
  id: number;
  name: string;
  units: FilterUnit[];
};

export type FilterPropertyGroup = {
  id: number | null;
  name: string;
  properties: FilterProperty[];
};

type UnitsFilterResponse = {
  property_groups: FilterPropertyGroup[];
  is_sites_and_communities: boolean;
};
type ListingViews = {
  total_views: number;
  unique_views: number;
};

export type ListingViewsResponse = ListingViews & {
  daily: Array<ListingViews & { date: string }>;
};

type OverviewResponse = {
  leads_created: number;
  days_on_market?: {
    average: number | null;
    median: number | null;
  };
  days_to_decision?: {
    average: number | null;
    median: number | null;
  };
  time_to_manual_contact?: {
    average: number | null;
    median: number | null;
  };
  touch_points_per_lead?: {
    average: number | null;
    median: number | null;
  };
  listing_views?: ListingViewsResponse;
  showings_scheduled: number;
  showings_completed: number;
  showings_no_shows: number;
  prescreening_criteria_failures: number;
};

export type ShowingDrilldownItem = {
  id: number;
  property_id: number;
  property_name: string;
  unit_id: number | null;
  unit_title: string | null;
  lead_id: number | null;
  lead_name: string | null;
  partner_user_id: number | null;
  partner_user_name: string | null;
  transitioned_at: string;
};

type ShowingDrilldownResponse = Paginated & {
  items: ShowingDrilldownItem[];
};

export type PrescreeningCriteriaFailureItem = {
  lead_id: number;
  lead_name: string | null;
  unit_id: number | null;
  unit_title: string | null;
  property_name: string | null;
  requested_at: string;
};

type PrescreeningCriteriaFailuresResponse = Paginated & {
  items: PrescreeningCriteriaFailureItem[];
};

type ShowingDrilldownParams = PaginatedDateRangeParams & {
  assignee_ids?: number[];
};

export type DaysOnMarketUnit = {
  id: number;
  property_name: string;
  property_address: string;
  property_id: number;
  unit_title: string;
  days_on_market: number | null;
  available_for_application: boolean;
};

type DaysOnMarketResponse = Paginated & {
  items: DaysOnMarketUnit[];
};

type PaginatedDateRangeParams = DateRangeParams & {
  page?: number;
  per_page?: number;
};

type LeadsResponse = Paginated & {
  items: Lead[];
};

export type LeadsBySourceItem = {
  source_id: number;
  source_name: string;
  color: string | null;
  count: number;
  percentage: number;
};

export type PipelineTransition = {
  stage_id: string | null;
  stage_name: string;
  count: number;
};

export type LeadPipelineSourceBreakdown = {
  source_id: number | null;
  source_name: string | null;
  count: number;
  percentage: number;
};

export type ApplicationPipelineStage = {
  stage_id: string;
  stage_name: string;
  total_count: number;
  transitions: PipelineTransition[];
};

export type LeadPipelineStage = ApplicationPipelineStage & {
  by_source: LeadPipelineSourceBreakdown[];
};

type LeadPipelineResponse = {
  stages: LeadPipelineStage[];
  truncated: boolean;
};

type LeadPipelineProperty = Pick<
  Property,
  "id" | "address" | "kind" | "owner" | "name" | "property_group"
>;

export type LeadPipelineDrilldownLead = {
  id: number;
  first_name: string;
  last_name: string;
  primary_email: string | null;
  primary_phone?: string | null;
  property: LeadPipelineProperty | null;
  unit: LeadPipelineUnit | null;
  created_at?: string | null;
  showing_requested_at?: string | null;
};

type LeadPipelineDrilldownResponse = Paginated & {
  items: LeadPipelineDrilldownLead[];
};

type PipelineDrilldownParams = {
  from_stage_id: string;
  to_stage_id: string;
  start_date?: string;
  end_date?: string;
  unit_ids?: number[];
  property_ids?: number[];
  property_group_ids?: number[];
  page?: number;
  per_page?: number;
};

export enum ApplicationStatus {
  STARTED = "started",
  PARTIALLY_SUBMITTED = "partially_submitted",
  SUBMITTED = "submitted",
  CONDITIONALLY_APPROVED = "approved_with_conditions",
  APPROVED = "approved",
  DECLINED = "rejected",
  ON_HOLD = "on_hold"
}

export const APPLICATION_STATUS_FUNNEL_ORDER: ApplicationStatus[] = [
  ApplicationStatus.STARTED,
  ApplicationStatus.PARTIALLY_SUBMITTED,
  ApplicationStatus.SUBMITTED,
  ApplicationStatus.CONDITIONALLY_APPROVED,
  ApplicationStatus.APPROVED,
  ApplicationStatus.DECLINED,
  ApplicationStatus.ON_HOLD
];

const APPLICATION_STATUS_SET = new Set<string>(
  Object.values(ApplicationStatus)
);

export const isApplicationStatus = (
  value: string
): value is ApplicationStatus => APPLICATION_STATUS_SET.has(value);

const APPLICATION_STATUS_COLOR: Record<ApplicationStatus, string> = {
  [ApplicationStatus.STARTED]: "#FCE2B2",
  [ApplicationStatus.PARTIALLY_SUBMITTED]: "#FE5DB5",
  [ApplicationStatus.SUBMITTED]: "#E6007E",
  [ApplicationStatus.CONDITIONALLY_APPROVED]: "#F59E0B",
  [ApplicationStatus.APPROVED]: "#4EBB32",
  [ApplicationStatus.DECLINED]: "#DE3C39",
  [ApplicationStatus.ON_HOLD]: "#B7B7B7"
};

export const getApplicationStatusColor = (
  status: string
): string | undefined =>
  isApplicationStatus(status) ? APPLICATION_STATUS_COLOR[status] : undefined;

type ApplicationPipelineTransitionDto = {
  status: ApplicationStatus | null;
  stage_name: string;
  count: number;
};

export type ApplicationPipelineStageDto = {
  status: ApplicationStatus;
  stage_name: string;
  total_count: number;
  transitions: ApplicationPipelineTransitionDto[];
};

type ApplicationPipelineResponse = {
  stages: ApplicationPipelineStageDto[];
  truncated: boolean;
};

export type ApplicationPipelineDrilldownItem = {
  id: number;
  name: string;
  property: LeadPipelineProperty | null;
  unit: LeadPipelineUnit | null;
  started_at?: string | null;
  submitted_at?: string | null;
  decision_at?: string | null;
};

type ApplicationPipelineDrilldownResponse = Paginated & {
  items: ApplicationPipelineDrilldownItem[];
};

type ApplicationPipelineDrilldownParams = Pick<
  PipelineDrilldownParams,
  | "start_date"
  | "end_date"
  | "unit_ids"
  | "property_ids"
  | "property_group_ids"
  | "page"
  | "per_page"
> & {
  from_status: ApplicationStatus;
  to_status: ApplicationStatus;
};

export const adaptApplicationStage = (
  stage: ApplicationPipelineStageDto
): ApplicationPipelineStage => ({
  stage_id: stage.status,
  stage_name: stage.stage_name,
  total_count: stage.total_count,
  transitions: stage.transitions.map((t) => ({
    stage_id: t.status,
    stage_name: t.stage_name,
    count: t.count
  }))
});

export type ReasonsForDeclineItem = {
  reason: string;
  count: number;
  percentage: string;
};

type ReasonsForDeclineResponse = Paginated & {
  items: ReasonsForDeclineItem[];
};

type ReasonsForDeclineParams = PaginatedDateRangeParams;

type ExportCsvParams = DateRangeParams & { timezone: string };

type ExportCsvStartResponse = { id: string; status: string };

type ExportCsvStatusResponse = {
  status: "pending" | "done" | "failed";
  download_url?: string;
  filename?: string;
  row_count?: number;
  truncated?: boolean;
  error?: string;
};

const BASE_URL = "/portal/v1/boom_crm/analytics";

const getShowingsDrilldown = async (
  path: string,
  params?: ShowingDrilldownParams
): Promise<ShowingDrilldownResponse> => {
  const { data } = await instance.get<ShowingDrilldownResponse>(
    `${BASE_URL}/${path}`,
    { params }
  );
  return data;
};

export type ProspectTopicTheme = {
  topic: string;
  representative_question: string;
  count: number;
};

type CommonProspectQuestionsResponse = {
  visible: boolean;
  max_count: number;
  themes: ProspectTopicTheme[];
};

export type ProspectTopicMessage = {
  message_id: string;
  conversation_id: string | null;
  contact_id: number;
  contact_name: string | null;
  channel: string;
  content: string;
  // The detected-question span, trimmed from `content`. Falls back to the full
  // content when the verbatim snippet could not be located.
  question_snippet: string;
  // Character offsets of the detected span within `content`; null when it could
  // not be located. Used to highlight + window the tooltip around the question.
  snippet_start: number | null;
  snippet_end: number | null;
  occurred_at: string;
};

type CommonProspectQuestionMessagesResponse = {
  total: number;
  avg_response_seconds: number | null;
  page: number;
  per_page: number;
  messages: ProspectTopicMessage[];
};

type ProspectQuestionMessagesParams = DateRangeParams & {
  topic: string;
  page?: number;
  per_page?: number;
};

export const crmAnalyticsApi = {
  getOverview: async (params?: DateRangeParams): Promise<OverviewResponse> => {
    const { data } = await instance.get<OverviewResponse>(
      `${BASE_URL}/overview`,
      {
        params
      }
    );
    return data;
  },

  getLeads: async (
    params?: PaginatedDateRangeParams
  ): Promise<LeadsResponse> => {
    const { data } = await instance.get<LeadsResponse>(`${BASE_URL}/leads`, {
      params
    });
    return data;
  },

  getDaysOnMarket: async (
    params?: PaginatedDateRangeParams
  ): Promise<DaysOnMarketResponse> => {
    const { data } = await instance.get<DaysOnMarketResponse>(
      `${BASE_URL}/days_on_market`,
      { params }
    );
    return data;
  },

  getLeadsBySource: async (
    params?: DateRangeParams
  ): Promise<LeadsBySourceItem[]> => {
    const { data } = await instance.get<LeadsBySourceItem[]>(
      `${BASE_URL}/leads_by_source`,
      { params }
    );
    return data;
  },

  getApprovalsBySource: async (
    params?: DateRangeParams
  ): Promise<LeadsBySourceItem[]> => {
    const { data } = await instance.get<LeadsBySourceItem[]>(
      `${BASE_URL}/approvals_by_source`,
      { params }
    );
    return data;
  },

  getLeadPipeline: async (
    params?: DateRangeParams
  ): Promise<LeadPipelineStage[]> => {
    const { data } = await instance.get<LeadPipelineResponse>(
      `${BASE_URL}/lead_pipeline`,
      { params }
    );
    return data.stages;
  },

  getLeadPipelineDrilldown: async (
    params: PipelineDrilldownParams
  ): Promise<LeadPipelineDrilldownResponse> => {
    const { data } = await instance.get<LeadPipelineDrilldownResponse>(
      `${BASE_URL}/lead_pipeline_drilldown`,
      { params }
    );
    return data;
  },

  getApplicationPipeline: async (
    params?: DateRangeParams
  ): Promise<ApplicationPipelineStage[]> => {
    const { data } = await instance.get<ApplicationPipelineResponse>(
      `${BASE_URL}/application_pipeline`,
      { params }
    );
    return data.stages.map(adaptApplicationStage);
  },

  getApplicationPipelineDrilldown: async (
    params: ApplicationPipelineDrilldownParams
  ): Promise<ApplicationPipelineDrilldownResponse> => {
    const { data } = await instance.get<ApplicationPipelineDrilldownResponse>(
      `${BASE_URL}/application_pipeline_drilldown`,
      { params }
    );
    return data;
  },

  getReasonsForDecline: async (
    params?: ReasonsForDeclineParams
  ): Promise<ReasonsForDeclineResponse> => {
    const { data } = await instance.get<ReasonsForDeclineResponse>(
      `${BASE_URL}/reasons_for_decline`,
      { params }
    );
    return data;
  },

  getListingViews: async (
    params?: DateRangeParams
  ): Promise<ListingViewsResponse> => {
    const { data } = await instance.get<ListingViewsResponse>(
      `${BASE_URL}/listing_views`,
      { params }
    );
    return data;
  },

  getShowingsScheduled: (
    params?: ShowingDrilldownParams
  ): Promise<ShowingDrilldownResponse> =>
    getShowingsDrilldown("showings_scheduled", params),

  getShowingsCompleted: (
    params?: ShowingDrilldownParams
  ): Promise<ShowingDrilldownResponse> =>
    getShowingsDrilldown("showings_completed", params),

  getShowingsNoShows: (
    params?: ShowingDrilldownParams
  ): Promise<ShowingDrilldownResponse> =>
    getShowingsDrilldown("showings_no_shows", params),

  getPrescreeningFailures: async (
    params?: PaginatedDateRangeParams
  ): Promise<PrescreeningCriteriaFailuresResponse> => {
    const { data } = await instance.get<PrescreeningCriteriaFailuresResponse>(
      `${BASE_URL}/prescreening_criteria_failures`,
      { params }
    );
    return data;
  },

  getUnitsFilter: async (params?: {
    available_only?: boolean;
    search?: string;
  }): Promise<UnitsFilterResponse> => {
    const { data } = await instance.get<UnitsFilterResponse>(
      `${BASE_URL}/units_filter`,
      { params }
    );
    return data;
  },

  getCommonProspectQuestions: async (
    params?: DateRangeParams
  ): Promise<CommonProspectQuestionsResponse> => {
    const { data } = await instance.get<CommonProspectQuestionsResponse>(
      `${BASE_URL}/common_prospect_questions`,
      { params }
    );
    return data;
  },

  getCommonProspectQuestionMessages: async (
    params: ProspectQuestionMessagesParams
  ): Promise<CommonProspectQuestionMessagesResponse> => {
    const { data } = await instance.get<CommonProspectQuestionMessagesResponse>(
      `${BASE_URL}/common_prospect_questions/messages`,
      { params }
    );
    return data;
  },

  // Returns an export id; the CSV itself arrives via S3.
  // `timezone` is required: without it the API formats every date column in UTC.
  exportCsv: async (
    params: ExportCsvParams
  ): Promise<ExportCsvStartResponse> => {
    const { data } = await instance.post<ExportCsvStartResponse>(
      `${BASE_URL}/export_csv`,
      params
    );
    return data;
  },

  getExportCsvStatus: async (id: string): Promise<ExportCsvStatusResponse> => {
    const { data } = await instance.get<ExportCsvStatusResponse>(
      `${BASE_URL}/export_csv_status`,
      { params: { id } }
    );
    return data;
  }
};
